// financlopediaData.js - Investopedia-level Financlopedia entries
// 16 priority terms with full SEO structure

/* ── Clusters ────────────────────────────────────────────────────────── */

const CLUSTERS = [
  {
    id: 'credito',
    name: 'Crédito',
    description: 'Todo lo que necesitas saber para construir y mantener tu historial crediticio en Canadá.',
    icon: 'CreditScore',
    color: '#1E3A5F',
    slugs: ['credit-score-canada', 'credit-bureau-canada', 'secured-card-canada', 'prime-rate-canada'],
  },
  {
    id: 'impuestos',
    name: 'Impuestos',
    description: 'Cómo funciona el sistema fiscal canadiense y qué beneficios puedes recibir.',
    icon: 'Receipt',
    color: '#D32F2F',
    slugs: ['cra-canada', 't4-canada', 'gst-hst-credit', 'child-benefit-canada', 'revenu-quebec-canada'],
  },
  {
    id: 'ahorro-inversion',
    name: 'Ahorro e Inversión',
    description: 'Cuentas registradas y estrategias para hacer crecer tu dinero en Canadá.',
    icon: 'Savings',
    color: '#2E7D32',
    slugs: ['tfsa-canada', 'rrsp-canada', 'fhsa-canada'],
  },
  {
    id: 'vivienda',
    name: 'Vivienda',
    description: 'Hipotecas, seguros y todo sobre comprar tu primera casa en Canadá.',
    icon: 'Home',
    color: '#F9A825',
    slugs: ['cmhc', 'amortización-canada'],
  },
  {
    id: 'vida-financiera',
    name: 'Vida Financiera',
    description: 'Conceptos esenciales del día a día financiero como recién llegado.',
    icon: 'AccountBalance',
    color: '#5F6B7A',
    slugs: ['sin-number-canada', 'interac-canada'],
  },
];

const financlopediaEntries = [
  {
    slug: 'tfsa-canada',
    term: 'TFSA',
    termFull: 'Tax-Free Savings Account',
    category: 'ahorro',
    difficulty: 'basico',
    readTime: 8,
    atomicAnswer:
      'La TFSA es una cuenta de ahorro canadiense donde todo lo que ganas - intereses, dividendos y ganancias de capital - es 100% libre de impuestos. Puedes retirar tu dinero en cualquier momento sin penalización. El límite anual de contribución en 2026 es de $7,000 CAD.',
    keyTakeaways: [
      'Todo lo que ganas dentro de la TFSA (intereses, dividendos, ganancias) esta libre de impuestos - para siempre.',
      'Puedes retirar dinero en cualquier momento sin impuestos ni penalizaciónes, y el espacio de contribución se recupera al año siguiente.',
      'El límite anual de contribución en 2026 es $7,000 CAD. Si nunca has contribuido, tu espacio acumulado puede superar $109,000.',
    ],
    metaDescription:
      'Que es la TFSA en Canadá? Cuenta de ahorro libre de impuestos explicada en español. Limites 2026, como abrir una, TFSA vs RRSP y más.',
    introduction:
      'La TFSA (Tax-Free Savings Account) es una de las herramientas financieras más poderosas disponibles en Canadá. A diferencia del RRSP, no reduces impuestos al depositar, pero todo lo que ganas dentro de la cuenta es permanentemente libre de impuestos. Para recién llegados latinos, la TFSA es el primer paso ideal para comenzar a construir patrimonio en Canadá.',
    sections: [
      {
        h2: '¿Cómo funciona la TFSA en Canadá?',
        content:
          '<p>La TFSA es una cuenta registrada por el gobierno federal donde puedes depositar dinero después de impuestos. Una vez dentro, cualquier ganancia - ya sea por intereses, dividendos de acciones, o apreciación de ETFs - esta completamente libre de impuestos.</p><p>La diferencia clave con una cuenta de ahorro normal: en una cuenta regular, pagas impuestos sobre los intereses ganados. En una TFSA, no pagas nada. Y cuando retiras, tampoco pagas impuestos.</p><p>Puedes abrir una TFSA en cualquier banco canadiense (TD, RBC, Scotiabank, Tangerine, etc.) o en plataformas de inversion como Wealthsimple o Questrade. Dentro de la TFSA puedes tener: cuentas de ahorro de alto interés (HISA), GICs, acciones, ETFs, bonos y fondos mutuos.</p>',
      },
      {
        h2: '¿Cuál es el límite de contribución de la TFSA en 2026?',
        content:
          '<p>El límite anual de contribución para 2026 es de <strong>$7,000 CAD</strong>. Pero tu espacio total disponible depende de cuántos años has sido residente fiscal de Canadá.</p><p>Si llegaste a Canadá en 2024, tu espacio acumulado es: $7,000 (2024) + $7,000 (2025) + $7,000 (2026) = $21,000. Si has sido residente desde 2009, tu espacio total puede superar $109,000.</p><p><strong>Importante:</strong> Si retiras dinero de tu TFSA, ese espacio se recupera al inicio del año siguiente. Por ejemplo, si retiras $5,000 en julio 2026, tendrás $5,000 adicionales de espacio en enero 2027.</p><p>Puedes verificar tu espacio exacto en <strong>CRA My Account</strong> o llamando a la CRA al 1-800-959-8281.</p>',
      },
      {
        h2: 'TFSA vs RRSP - ¿cuál conviene más para recién llegados?',
        content:
          '<p>Esta es la pregunta más común entre recién llegados. La respuesta depende de tu situación:</p><p><strong>Usa la TFSA primero si:</strong></p><ul><li>Tu salario es menor a $55,000/ano - el beneficio fiscal del RRSP es menor en brackets bajos</li><li>Necesitas flexibilidad para retirar dinero sin penalización</li><li>Estas ahorrando para un fondo de emergencia o una meta a corto plazo</li><li>Acabas de llegar y no estas seguro de quedarte permanentemente</li></ul><p><strong>Usa el RRSP primero si:</strong></p><ul><li>Tu salario supera $55,000/ano - mayor ahorro fiscal inmediato</li><li>Tu empleador ofrece RRSP matching (dinero gratis)</li><li>Planeas usar el Home Buyers Plan para comprar casa</li></ul><p>La estrategia ideal para la mayoría: maximiza tu TFSA primero, luego contribuye al RRSP con lo que sobre.</p>',
      },
      {
        h2: '¿Cómo abrir una TFSA como recién llegado a Canadá?',
        content:
          '<p>Abrir una TFSA es sencillo y se puede hacer el mismo día que abres tu cuenta bancaria. Necesitas:</p><ul><li><strong>SIN (Social Insurance Number)</strong> - obligatorio</li><li><strong>Ser mayor de 18 años</strong> (19 en algunas provincias)</li><li><strong>Ser residente de Canadá</strong> - residentes permanentes, trabajadores temporales y refugiados califican</li></ul><p><strong>Paso a paso:</strong></p><ol><li>Visita cualquier sucursal bancaria o abre una cuenta en línea (Tangerine, EQ Bank, Wealthsimple)</li><li>Solicita abrir una TFSA - la mayoría de bancos lo ofrecen como parte del paquete de cuenta</li><li>Elige el tipo de TFSA: cuenta de ahorro (bajo riesgo) o cuenta de inversion (mayor potencial)</li><li>Deposita hasta tu límite disponible</li></ol><p><strong>Consejo:</strong> Si solo quieres ahorrar sin riesgo, elige un TFSA de ahorro de alto interés (HISA). Si quieres invertir a largo plazo, abre un TFSA de inversion con ETFs diversificados.</p>',
      },
    ],
    relatedTerms: ['rrsp-canada', 'fhsa-canada', 'credit-score-canada', 'cra-canada', 'gst-hst-credit'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Mejores cuentas TFSA para recién llegados',
      description: 'Compara las mejores opciones de TFSA en Canadá: tasas de interés, comisiones y requisitos para recién llegados.',
      ctaText: 'Comparar cuentas',
      ctaLink: '/banca',
    },
    faqs: [
      {
        question: '¿Puedo tener una TFSA si soy residente temporal en Canadá?',
        answer:
          'Si, siempre que tengas un SIN valido y seas mayor de 18 años. Tu espacio de contribución empieza a acumularse desde el año en que te convertiste en residente fiscal de Canadá.',
      },
      {
        question: '¿Qué pasa si contribuyo de más a mi TFSA?',
        answer:
          'La CRA te cobra un impuesto del 1% mensual sobre el exceso hasta que lo retires. Es importante llevar control de tu espacio disponible en CRA My Account.',
      },
      {
        question: '¿Se puede perder dinero en una TFSA?',
        answer:
          'Si inviertes en acciones o ETFs dentro de tu TFSA, el valor puede bajar. Si solo usas una cuenta de ahorro de alto interés (HISA) dentro de tu TFSA, tu dinero esta protegido por CDIC hasta $100,000.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['rrsp-canada', 'fhsa-canada', 'cra-canada'],
    comparatorLinks: [{ label: 'Comparar cuentas de ahorro', path: '/comparar-cuentas' }],
    sources: [
      { label: 'CRA - Tax-Free Savings Account', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html' },
      { label: 'Government of Canada - TFSA Contribution Limits', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account/contributions.html' },
    ],
  },
  {
    slug: 'rrsp-canada',
    term: 'RRSP',
    termFull: 'Registered Retirement Savings Plan',
    category: 'ahorro',
    difficulty: 'intermedio',
    readTime: 9,
    atomicAnswer:
      'El RRSP es una cuenta de ahorro para el retiro en Canadá que te permite reducir tu ingreso imponible hoy. Cada dolar que depositas se deduce de tus impuestos. El dinero crece libre de impuestos dentro de la cuenta hasta que lo retiras en la jubilación.',
    keyTakeaways: [
      'Cada dolar que depositas reduce tu ingreso imponible del año, lo que significa que pagas menos impuestos ahora.',
      'El dinero crece libre de impuestos dentro del RRSP - solo pagas impuestos cuando retiras, idealmente en la jubilación cuando tu ingreso es menor.',
      'El límite de contribución es 18% de tu ingreso del año anterior, hasta un máximo de ~$33,810 CAD en 2026. El espacio no usado se acumula.',
    ],
    metaDescription:
      'Que es el RRSP en Canadá? Plan de ahorro para el retiro explicado en español. Limites 2026, beneficios fiscales, RRSP vs TFSA para latinos.',
    introduction:
      'El RRSP (Registered Retirement Savings Plan) es la cuenta de ahorro para el retiro más popular en Canadá. Su principal ventaja: cada dolar que depositas reduce tus impuestos inmediatamente. Para recién llegados latinos con salarios por encima de $55,000, el RRSP puede significar ahorros fiscales de $1,000 a $5,000 cada año.',
    sections: [
      {
        h2: '¿Cómo funciona el beneficio fiscal del RRSP?',
        content:
          '<p>El RRSP funciona con un principio simple: <strong>reduces impuestos hoy, pagas impuestos manana</strong>. Cuando depositas dinero en tu RRSP, ese monto se deduce de tu ingreso imponible del año.</p><p><strong>Ejemplo práctico:</strong> Si ganas $70,000 al año y depositas $10,000 en tu RRSP, tu ingreso imponible baja a $60,000. Si estas en un bracket de impuestos del 30%, eso significa un ahorro de $3,000 en impuestos - dinero que te devuelve la CRA en tu declaración.</p><p>Dentro del RRSP, tu dinero crece sin impuestos: intereses, dividendos, ganancias de capital - todo se acumula sin que el gobierno tome su parte. Solo pagas impuestos cuando retiras en la jubilación, idealmente cuando tu ingreso (y tu tasa de impuestos) sea mucho menor.</p>',
      },
      {
        h2: '¿Cuál es el límite de contribución al RRSP en 2026?',
        content:
          '<p>El límite de contribución al RRSP para 2026 es <strong>18% de tu ingreso del año anterior</strong>, hasta un máximo de aproximadamente <strong>$33,810 CAD</strong>.</p><p>Si no usas todo tu espacio en un año, se acumula para años futuros. Puedes ver tu límite exacto de contribución en:</p><ul><li><strong>CRA My Account</strong> - seccion "RRSP and TFSA"</li><li><strong>Tu Notice of Assessment (NOA)</strong> - la carta que recibes después de declarar impuestos</li></ul><p><strong>Cuidado:</strong> Si contribuyes de más, la CRA te cobra un impuesto del 1% mensual sobre el exceso (se permite un colchon de $2,000).</p>',
      },
      {
        h2: '¿Puedo retirar dinero de mi RRSP antes de jubilarme?',
        content:
          '<p>Si, pero con consecuencias fiscales. Los retiros regulares del RRSP se suman a tu ingreso imponible del año, y el banco retiene un impuesto automático:</p><ul><li>Hasta $5,000: 10% de retención</li><li>$5,001 a $15,000: 20% de retención</li><li>Mas de $15,000: 30% de retención</li></ul><p><strong>Excepciones sin impuesto inmediato:</strong></p><ul><li><strong>Home Buyers Plan (HBP):</strong> Retira hasta $60,000 para comprar tu primera casa. Debes repagar en 15 años.</li><li><strong>Lifelong Learning Plan (LLP):</strong> Retira hasta $20,000 para educación. Debes repagar en 10 años.</li></ul>',
      },
      {
        h2: '¿Qué pasa con mi RRSP si me voy de Canadá?',
        content:
          '<p>Si dejas de ser residente fiscal de Canadá, tienes opciones:</p><ul><li><strong>Dejar el RRSP abierto:</strong> El dinero sigue creciendo libre de impuestos. Cuando retires, se aplicara un impuesto de retención del 25% (puede variar según tratados fiscales con tu país).</li><li><strong>Cerrar el RRSP:</strong> Se aplica el impuesto de retención sobre el monto total. Es generalmente la peor opción fiscal.</li><li><strong>Transferir a un RRIF:</strong> Puedes convertir tu RRSP en un fondo de ingreso de jubilación y retirar gradualmente.</li></ul><p><strong>Consejo:</strong> Consulta con un contador especializado en impuestos internacionales antes de tomar esta decision. Los tratados fiscales entre Canadá y países como Colombia, México o Chile pueden reducir significativamente los impuestos.</p>',
      },
    ],
    relatedTerms: ['tfsa-canada', 'fhsa-canada', 'gst-hst-credit', 'cra-canada', 't4-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Mejores cuentas RRSP para recién llegados',
      description: 'Compara opciones de RRSP con las mejores tasas de retorno y sin comisiones para recién llegados.',
      ctaText: 'Comparar RRSP',
      ctaLink: '/banca',
    },
    faqs: [
      {
        question: '¿Cuánto puedo ahorrar en impuestos con el RRSP?',
        answer:
          'Depende de tu bracket de impuestos. Si ganas $70,000 y contribuyes $10,000, puedes ahorrar aproximadamente $3,000 en impuestos. Si ganas $100,000, el ahorro puede ser de $4,000-$5,000 por la misma contribución.',
      },
      {
        question: '¿Mi empleador ofrece RRSP matching - debo aceptar?',
        answer:
          'Absolutamente. El RRSP matching del empleador es dinero gratis. Si tu empleador iguala tu contribución hasta un porcentaje, SIEMPRE contribuye al menos hasta ese tope. Es un retorno inmediato del 100%.',
      },
      {
        question: '¿El RRSP afecta mis beneficios como el Child Benefit?',
        answer:
          'Si, positivamente. Al reducir tu ingreso imponible con contribuciones al RRSP, puedes calificar para mayor Canada Child Benefit y GST/HST Credit, ya que estos se calculan sobre tu ingreso neto.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['tfsa-canada', 'fhsa-canada', 'cra-canada'],
    comparatorLinks: [{ label: 'Comparar cuentas RRSP', path: '/comparar-cuentas' }],
    sources: [
      { label: 'CRA - Registered Retirement Savings Plan', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans/registered-retirement-savings-plan-rrsp.html' },
      { label: 'Government of Canada - RRSP Contribution Limits', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans/contributing-a-rrsp-prpp.html' },
    ],
  },
  {
    slug: 'fhsa-canada',
    term: 'FHSA',
    termFull: 'First Home Savings Account',
    category: 'ahorro',
    difficulty: 'basico',
    readTime: 7,
    atomicAnswer:
      'La FHSA combina lo mejor del RRSP y la TFSA: reduces impuestos al depositar Y retiras libre de impuestos para comprar tu primera casa en Canadá. Límite anual de $8,000 y máximo de por vida de $40,000.',
    keyTakeaways: [
      'Única cuenta en Canadá que ofrece deduccion fiscal al depositar Y retiro libre de impuestos - lo mejor del RRSP y la TFSA combinados.',
      'Límite anual de $8,000 con un máximo de por vida de $40,000. Si no usas todo el espacio, puedes trasladar hasta $8,000 al siguiente año.',
      'Si nunca compras casa, puedes transferir los fondos a tu RRSP sin afectar tu espacio de contribución.',
    ],
    metaDescription:
      'Que es la FHSA en Canadá? Cuenta para comprar tu primera casa explicada en español. Limites, requisitos y como abrirla como recién llegado.',
    introduction:
      'La FHSA (First Home Savings Account) es la cuenta más nueva del gobierno canadiense, creada en 2023 específicamente para ayudar a compradores de primera vivienda. Es el único producto financiero en Canadá que te da doble beneficio fiscal: deduccion al depositar y retiro libre de impuestos.',
    sections: [
      {
        h2: '¿Cómo funciona la FHSA para comprar tu primera casa?',
        content:
          '<p>La FHSA funciona como una combinacion perfecta del RRSP y la TFSA:</p><ul><li><strong>Como el RRSP:</strong> Cada dolar que depositas reduce tu ingreso imponible. Si depositas $8,000 y estas en un bracket del 30%, ahorras $2,400 en impuestos.</li><li><strong>Como la TFSA:</strong> Cuando retiras para comprar tu casa, no pagas ningun impuesto sobre las ganancias ni sobre el monto retirado.</li></ul><p>El dinero dentro de la FHSA puede invertirse en GICs, acciones, ETFs, bonos - las mismas opciones que en una TFSA o RRSP.</p>',
      },
      {
        h2: '¿Quién puede abrir una FHSA en Canadá?',
        content:
          '<p>Para abrir una FHSA necesitas cumplir <strong>todos</strong> estos requisitos:</p><ul><li>Ser residente de Canadá</li><li>Tener 18 años o más (19 en algunas provincias)</li><li><strong>No haber sido dueno de una vivienda</strong> en el año de apertura ni en los 4 años anteriores</li><li>Tener un SIN valido</li></ul><p>La cuenta se puede mantener abierta hasta por 15 años o hasta que cumplas 71 años (lo que ocurra primero). Si no compras casa en ese periodo, puedes transferir los fondos a un RRSP.</p><p><strong>Buena noticia para recién llegados:</strong> Si nunca has sido dueno de propiedad en Canadá (aunque hayas tenido una en tu país de origen), generalmente calificas.</p>',
      },
      {
        h2: '¿Cuánto puedo ahorrar en mi FHSA?',
        content:
          '<p>Los límites de la FHSA son:</p><ul><li><strong>Límite anual:</strong> $8,000</li><li><strong>Máximo de por vida:</strong> $40,000</li><li><strong>Carry-forward:</strong> Si no usas los $8,000 completos en un año, puedes trasladar hasta $8,000 al siguiente año</li></ul><p><strong>Ejemplo:</strong> Si abres tu FHSA en 2026 y depositas solo $5,000, en 2027 podras depositar hasta $11,000 ($8,000 + $3,000 de carry-forward).</p><p>En 5 años, maximizando tus contribuciones, tendrás $40,000 + las ganancias de inversion - todo listo para el enganche de tu casa.</p>',
      },
      {
        h2: 'FHSA vs RRSP Home Buyers Plan - ¿cuál es mejor?',
        content:
          '<p>Ambas te ayudan a comprar tu primera casa, pero funcionan diferente:</p><table><tr><th>Característica</th><th>FHSA</th><th>RRSP HBP</th></tr><tr><td>Deduccion fiscal</td><td>Si</td><td>Si</td></tr><tr><td>Retiro libre de impuestos</td><td>Si</td><td>No - debes repagar en 15 años</td></tr><tr><td>Límite</td><td>$40,000</td><td>$60,000</td></tr><tr><td>Repago</td><td>No requiere</td><td>15 años</td></tr></table><p><strong>Estrategia optima:</strong> Usa ambos. Maximiza tu FHSA ($40,000) y complementa con el HBP ($60,000). Puedes combinar hasta $100,000 de enganche con ventajas fiscales.</p>',
      },
    ],
    relatedTerms: ['tfsa-canada', 'rrsp-canada', 'amortización-canada', 'prime-rate-canada', 'cmhc'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Abre tu FHSA hoy',
      description: 'Compara las mejores opciones para abrir tu FHSA con las tasas más altas y sin comisiones.',
      ctaText: 'Comparar opciones',
      ctaLink: '/banca',
    },
    faqs: [
      {
        question: '¿Puedo tener FHSA y RRSP al mismo tiempo?',
        answer:
          'Si. Son cuentas independientes con límites separados. De hecho, la estrategia optima es maximizar ambas para comprar tu primera casa.',
      },
      {
        question: '¿Qué pasa si nunca compro casa?',
        answer:
          'Puedes transferir los fondos a tu RRSP sin afectar tu espacio de contribución. También puedes retirar los fondos (pagando impuestos como un RRSP normal).',
      },
      {
        question: '¿Puedo usar la FHSA para comprar un condo?',
        answer:
          'Si. La FHSA aplica para cualquier propiedad que califique como vivienda principal: casa, condo, townhouse, o incluso una participacion cooperativa.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['amortización-canada', 'cmhc', 'credit-score-canada'],
    comparatorLinks: [{ label: 'Calculadora hipotecaria', path: '/calculadora-hipoteca' }, { label: 'Comparar hipotecas', path: '/comparar-hipotecas' }],
    sources: [
      { label: 'CRA - First Home Savings Account', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html' },
      { label: 'Government of Canada - FHSA Overview', url: 'https://www.canada.ca/en/department-finance/news/2022/08/tax-free-first-home-savings-account.html' },
    ],
  },
  {
    slug: 'sin-number-canada',
    term: 'SIN Number',
    termFull: 'Social Insurance Number',
    category: 'vida',
    difficulty: 'basico',
    readTime: 6,
    atomicAnswer:
      'El SIN (Social Insurance Number) es un número de 9 digitos que necesitas para trabajar legalmente en Canadá, declarar impuestos y acceder a beneficios del gobierno. Es el equivalente al número de seguro social y se obtiene gratis en Service Canada.',
    keyTakeaways: [
      'Es obligatorio para trabajar legalmente, declarar impuestos, abrir cuentas bancarias y acceder a beneficios como el Child Benefit o GST Credit.',
      'Se obtiene gratis y generalmente el mismo día en oficinas de Service Canada. También se puede solicitar en línea.',
      'Los SIN que empiezan con 9 son temporales (para permisos de trabajo/estudio) y se actualizan al obtener residencia permanente.',
    ],
    metaDescription:
      'Que es el SIN Number en Canadá? Como obtenerlo como recién llegado, requisitos, y por qué es esencial. Guía en español para latinos.',
    introduction:
      'El SIN (Social Insurance Number) es tu identificacion fiscal en Canadá. Sin el, no puedes trabajar, declarar impuestos ni recibir beneficios del gobierno. Es uno de los primeros tramites que debes hacer al llegar a Canadá.',
    sections: [
      {
        h2: '¿Qué es el SIN y para qué se necesita?',
        content:
          '<p>El SIN es un número único de 9 digitos asignado por el gobierno de Canadá. Lo necesitas para:</p><ul><li><strong>Trabajar legalmente</strong> - tu empleador lo necesita para pagarte y reportar a la CRA</li><li><strong>Declarar impuestos</strong> - sin SIN no puedes presentar tu T1</li><li><strong>Abrir cuentas bancarias</strong> - los bancos lo piden para cuentas de inversion (TFSA, RRSP)</li><li><strong>Recibir beneficios</strong> - Child Benefit, GST Credit, EI (Employment Insurance)</li><li><strong>Solicitar crédito</strong> - tarjetas de crédito y préstamos</li></ul>',
      },
      {
        h2: '¿Cómo obtener tu SIN al llegar a Canadá?',
        content:
          '<p><strong>Opcion 1: En persona (más rápido)</strong></p><ol><li>Visita una oficina de Service Canada</li><li>Lleva tu pasaporte + permiso de trabajo, estudio o tarjeta de residencia permanente</li><li>Llena el formulario en la oficina</li><li>Recibe tu SIN generalmente el mismo día</li></ol><p><strong>Opcion 2: En línea</strong></p><ol><li>Visita el portal de Service Canada</li><li>Necesitas un documento de identidad y tu permiso de inmigracion</li><li>Recibes tu SIN por correo en 2-3 semanas</li></ol><p>El tramite es <strong>completamente gratuito</strong>.</p>',
      },
      {
        h2: '¿Qué significa que mi SIN empiece con 9?',
        content:
          '<p>Los SIN que empiezan con <strong>9</strong> son temporales y se asignan a personas con permisos de trabajo o estudio temporales. Tienen una fecha de vencimiento que coincide con tu permiso.</p><p>Cuando obtienes la <strong>residencia permanente</strong>, Service Canada te asigna un nuevo SIN permanente (que NO empieza con 9). Este nuevo número no vence nunca.</p><p><strong>Importante:</strong> Si tu permiso de trabajo se renueva, también debes renovar tu SIN temporal en Service Canada.</p>',
      },
      {
        h2: '¿Cómo proteger tu SIN de fraude?',
        content:
          '<p>Tu SIN es información extremadamente sensible. Protegelo así:</p><ul><li><strong>Nunca lo cargues en tu billetera</strong> - memoriza el número</li><li><strong>Solo compartelo cuando sea legalmente requerido:</strong> empleador, banco, CRA, Revenu Quebec</li><li><strong>No lo des a arrendadores</strong> - no es obligatorio para rentar un apartamento</li><li><strong>Revisa tu crédito regularmente</strong> - si alguien usa tu SIN fraudulentamente, aparecera en tu reporte de crédito</li></ul><p>Si sospechas fraude, contacta a Service Canada y a los buros de crédito (Equifax y TransUnion) inmediatamente.</p>',
      },
    ],
    relatedTerms: ['cra-canada', 't4-canada', 'gst-hst-credit', 'child-benefit-canada', 'credit-score-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Abre tu primera cuenta bancaria en Canadá',
      description: 'Una vez tengas tu SIN, abre una cuenta bancaria. Compara las mejores opciones para recién llegados.',
      ctaText: 'Comparar cuentas',
      ctaLink: '/banca',
    },
    faqs: [
      {
        question: '¿Puedo trabajar mientras espero mi SIN?',
        answer:
          'Tecnicamente no. Necesitas tener tu SIN para que tu empleador pueda contratarte legalmente. Por eso es recomendable obtenerlo lo antes posible al llegar.',
      },
      {
        question: '¿Cuánto cuesta obtener el SIN?',
        answer:
          'Es completamente gratuito. Si alguien te cobra por obtener tu SIN, es una estafa.',
      },
      {
        question: '¿Necesito un SIN para abrir una cuenta bancaria?',
        answer:
          'Puedes abrir una cuenta basica sin SIN, pero lo necesitas para cuentas de inversion como TFSA o RRSP, y para recibir intereses reportables.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['cra-canada', 'credit-score-canada', 'tfsa-canada'],
    comparatorLinks: [{ label: 'Comparar cuentas bancarias', path: '/comparar-cuentas' }],
    sources: [
      { label: 'Service Canada - Social Insurance Number', url: 'https://www.canada.ca/en/employment-social-development/services/sin.html' },
      { label: 'CRA - Your Social Insurance Number', url: 'https://www.canada.ca/en/employment-social-development/services/sin/reports/code-of-practice.html' },
    ],
  },
  {
    slug: 'credit-score-canada',
    term: 'Credit Score',
    termFull: 'Puntaje de Crédito Canadiense',
    category: 'credito',
    difficulty: 'basico',
    readTime: 9,
    atomicAnswer:
      'Tu credit score en Canadá es un número entre 300 y 900 que mide tu confiabilidad financiera. Un puntaje de 680+ se considera bueno. Lo usan bancos, arrendadores y empleadores para evaluarte. Tu historial de tu país NO se transfiere - en Canadá empiezas desde cero.',
    keyTakeaways: [
      'El rango va de 300 a 900. Un puntaje de 680+ es bueno, 750+ es excelente. Se calcula considerando historial de pagos (35%), uso del crédito (30%), antiguedad (15%), tipos (10%) y consultas (10%).',
      'Tu historial crediticio de tu país de origen NO se transfiere a Canadá. Empiezas desde cero sin importar tu historial previo.',
      'La forma más rápida de construir crédito es con una tarjeta asegurada (secured card), usandola para compras pequenas y pagando el saldo completo cada mes.',
    ],
    metaDescription:
      'Cómo funciona el credit score en Canadá? Guía completa en español. Como construir crédito desde cero como recién llegado, rangos y consejos.',
    introduction:
      'El credit score es uno de los números más importantes de tu vida financiera en Canadá. Determina si puedes obtener una tarjeta de crédito, rentar un apartamento, obtener una hipoteca, e incluso conseguir ciertos trabajos. Para los latinos que llegan a Canadá, entender y construir el credit score es una prioridad desde el primer día.',
    sections: [
      {
        h2: '¿Cómo se calcula el credit score en Canadá?',
        content:
          '<p>Tu puntaje se calcula usando 5 factores principales:</p><ul><li><strong>Historial de pagos (35%):</strong> Pagar a tiempo es el factor más importante</li><li><strong>Uso del crédito (30%):</strong> Cuanto usas de tu límite disponible. Idealmente menos del 30%</li><li><strong>Antiguedad de cuentas (15%):</strong> Cuentas más antiguas = mejor puntaje</li><li><strong>Tipos de crédito (10%):</strong> Tener una mezcla de creditos (tarjeta, línea de crédito, préstamo) ayuda</li><li><strong>Consultas recientes (10%):</strong> Muchas solicitudes de crédito en poco tiempo bajan tu puntaje temporalmente</li></ul><p>Los rangos tipicos son: <strong>300-559</strong> (pobre), <strong>560-659</strong> (regular), <strong>660-724</strong> (bueno), <strong>725-759</strong> (muy bueno), <strong>760-900</strong> (excelente).</p>',
      },
      {
        h2: '¿Cómo construir credit score desde cero como recién llegado?',
        content:
          '<p>Al llegar a Canadá, tu credit score no existe. Aquí esta el plan paso a paso:</p><ol><li><strong>Mes 1:</strong> Obtiene una tarjeta de crédito asegurada (secured card). Depositas $300-$500 como garantia</li><li><strong>Meses 1-6:</strong> Usa la tarjeta para compras pequenas ($50-$100/mes) y paga el saldo COMPLETO antes de la fecha límite</li><li><strong>Mes 6:</strong> Tu score deberia estar alrededor de 650-680</li><li><strong>Mes 6-12:</strong> Solicita una tarjeta no asegurada. Mantiene la secured card abierta para mantener la antiguedad</li><li><strong>Mes 12+:</strong> Con un score de 700+, calificas para la mayoría de productos financieros</li></ol><p><strong>Regla de oro:</strong> NUNCA uses más del 30% de tu límite. Si tu límite es $500, no gastes más de $150 al mes en esa tarjeta.</p>',
      },
      {
        h2: '¿Dónde puedo revisar mi credit score gratis en Canadá?',
        content:
          '<p>Existen dos buros de crédito en Canadá: <strong>Equifax</strong> y <strong>TransUnion</strong>. Puedes revisar tu score gratis a traves de:</p><ul><li><strong>Borrowell</strong> - usa datos de Equifax, gratis y no afecta tu score</li><li><strong>Credit Karma</strong> - usa datos de TransUnion, también gratis</li></ul><p>Revisar tu propio score es un "soft check" que <strong>NO afecta tu puntaje</strong>. Solo las consultas de prestamistas ("hard checks") pueden bajarlo ligeramente.</p><p>Tu score puede ser diferente entre Equifax y TransUnion porque no todos los prestamistas reportan a ambas agencias.</p>',
      },
      {
        h2: '¿Qué errores comunes bajan el credit score?',
        content:
          '<p>Evita estos errores que destruyen tu credit score:</p><ul><li><strong>Pagar tarde:</strong> Un solo pago atrasado puede bajar tu score 50-100 puntos y permanece en tu reporte 6 años</li><li><strong>Maxear tu tarjeta:</strong> Usar más del 30% de tu límite baja tu score significativamente</li><li><strong>Solicitar muchas tarjetas a la vez:</strong> Cada solicitud genera un hard check</li><li><strong>Cerrar tarjetas antiguas:</strong> Reduces tu antiguedad promedio y tu crédito disponible</li><li><strong>No tener ninguna tarjeta:</strong> Sin actividad crediticia, no se construye score</li></ul>',
      },
    ],
    relatedTerms: ['secured-card-canada', 'credit-bureau-canada', 'cra-canada', 'sin-number-canada', 'interac-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Mejores tarjetas para construir crédito',
      description: 'Compara tarjetas aseguradas y no aseguradas disenadas para recién llegados a Canadá.',
      ctaText: 'Comparar tarjetas',
      ctaLink: '/tarjetas-de-credito',
    },
    faqs: [
      {
        question: '¿Mi credit score de mi país cuenta en Canadá?',
        answer:
          'No. Tu historial crediticio no se transfiere entre países. En Canadá empiezas desde cero, sin importar tu historial previo.',
      },
      {
        question: '¿Cuánto tiempo tarda en construir un buen credit score?',
        answer:
          'Con una tarjeta asegurada y pagos puntuales, puedes tener un score de 680+ en 6-8 meses. Un score de 750+ generalmente toma 12-18 meses.',
      },
      {
        question: '¿Pagar la renta ayuda a mi credit score?',
        answer:
          'Tradicionalmente no, porque los arrendadores no reportan a los buros. Sin embargo, servicios como Borrowell Rent Advantage ahora te permiten reportar tus pagos de renta.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['secured-card-canada', 'credit-bureau-canada', 'prime-rate-canada'],
    comparatorLinks: [{ label: 'Comparar tarjetas de crédito', path: '/tarjetas-de-credito' }],
    sources: [
      { label: 'Financial Consumer Agency of Canada - Credit Score', url: 'https://www.canada.ca/en/financial-consumer-agency/services/credit-reports-score/understanding-credit-score.html' },
      { label: 'Equifax Canada', url: 'https://www.consumer.equifax.ca/personal/' },
    ],
  },
  {
    slug: 't4-canada',
    term: 'T4',
    termFull: 'Statement of Remuneration Paid',
    category: 'impuestos',
    difficulty: 'basico',
    readTime: 5,
    atomicAnswer:
      'El T4 es el formulario que tu empleador te entrega cada febrero con el resumen de tu salario anual, impuestos retenidos y contribuciones al CPP y EI. Lo necesitas para declarar tus impuestos ante la CRA.',
    keyTakeaways: [
      'Tu empleador esta obligado a entregarte el T4 antes del 28 de febrero. Si tuviste varios empleos, recibiras un T4 de cada uno.',
      'Contiene tu ingreso bruto, impuestos federales y provinciales retenidos, contribuciones al CPP/QPP y primas de EI.',
      'Puedes ver tus T4 en CRA My Account a partir de marzo si tu empleador los reportó electrónicamente.',
    ],
    metaDescription:
      'Que es el T4 en Canadá? Formulario de impuestos explicado en español. Como leerlo, cuando recibirlo y que hacer si no lo recibes.',
    introduction:
      'El T4 es el documento fiscal más importante para empleados en Canadá. Es el equivalente a la constancia de retención de tu país. Sin el, no puedes declarar impuestos correctamente.',
    sections: [
      {
        h2: '¿Qué información contiene el formulario T4?',
        content:
          '<p>Cada casilla del T4 contiene información especifica:</p><ul><li><strong>Box 14:</strong> Tu ingreso total de empleo (salario bruto)</li><li><strong>Box 16:</strong> Contribuciones al CPP (Canadá Pension Plan) o QPP en Quebec</li><li><strong>Box 18:</strong> Primas de EI (Employment Insurance)</li><li><strong>Box 22:</strong> Impuesto federal retenido</li><li><strong>Box 44:</strong> Cuotas sindicales (si aplica)</li><li><strong>Box 52:</strong> Contribuciones del empleador al RRSP</li></ul><p>El T4 resume TODO tu año laboral con ese empleador en una sola página.</p>',
      },
      {
        h2: '¿Cuándo debo recibir mi T4?',
        content:
          '<p>Tu empleador esta legalmente obligado a entregarte el T4 antes del <strong>28 de febrero</strong> del año siguiente al año fiscal.</p><p>La mayoría de empleadores grandes lo envian por correo o lo ponen disponible en el portal de nomina. También puedes acceder a tu T4 en <strong>CRA My Account</strong> a partir de mediados de marzo.</p>',
      },
      {
        h2: '¿Qué hago si no recibo mi T4?',
        content:
          '<p>Si no recibes tu T4 para mediados de marzo:</p><ol><li><strong>Contacta a tu empleador</strong> directamente y pide una copia</li><li><strong>Revisa CRA My Account</strong> - puede estar disponible electrónicamente</li><li><strong>Llama a la CRA</strong> al 1-800-959-8281 si tu empleador no responde</li><li><strong>Usa tus talones de pago</strong> como respaldo si necesitas declarar antes del 30 de abril</li></ol>',
      },
      {
        h2: '¿Cuál es la diferencia entre T4, T4A y T5?',
        content:
          '<p>Existen diferentes formularios fiscales:</p><ul><li><strong>T4:</strong> Ingreso de empleo regular (salario)</li><li><strong>T4A:</strong> Otros ingresos - pensiones, becas, freelance, comisiones. También CERB si lo recibiste</li><li><strong>T5:</strong> Ingresos de inversiones - intereses, dividendos de cuentas NO registradas</li><li><strong>T3:</strong> Ingresos de fondos mutuos y fideicomisos</li></ul><p>Todos se necesitan para tu declaración. Asegúrate de recibir todos los que te correspondan.</p>',
      },
    ],
    relatedTerms: ['cra-canada', 'revenu-quebec-canada', 'sin-number-canada', 'gst-hst-credit', 'rrsp-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Declara tus impuestos gratis',
      description: 'Usa Wealthsimple Tax para declarar gratis. Ideal para declaraciones sencillas de empleados.',
      ctaText: 'Declarar gratis',
      ctaLink: '/herramientas',
    },
    faqs: [
      {
        question: '¿Necesito declarar impuestos si trabaje solo unos meses?',
        answer: 'Si. Incluso si trabajaste solo un mes, debes declarar. Además, es probable que te devuelvan impuestos porque te retuvieron asumiendo que ganerias más.',
      },
      {
        question: '¿Qué hago si mi T4 tiene un error?',
        answer: 'Contacta a tu empleador inmediatamente para que emita un T4 corregido (T4 amended). No declares con información incorrecta.',
      },
      {
        question: '¿Puedo declarar impuestos sin T4?',
        answer: 'Si, usando tus talones de pago como referencia. Pero es mejor esperar al T4 oficial para evitar discrepancias con la CRA.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['cra-canada', 'gst-hst-credit', 'rrsp-canada'],
    comparatorLinks: [],
    sources: [
      { label: 'CRA - T4 Information', url: 'https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/payroll/completing-filing-information-returns/t4-information-employers.html' },
      { label: 'CRA - Get a Copy of Your T4', url: 'https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-individuals/account-individuals.html' },
    ],
  },
  {
    slug: 'interac-canada',
    term: 'Interac',
    termFull: 'Red de Pagos y Transferencias de Canadá',
    category: 'vida',
    difficulty: 'basico',
    readTime: 5,
    atomicAnswer:
      'Interac es la red de pagos con tarjeta de débito en Canadá. Permite pagar en tiendas directamente desde tu cuenta bancaria y enviar transferencias instantaneas entre personas con Interac e-Transfer usando solo un email o número de teléfono.',
    keyTakeaways: [
      'Interac Debit te permite pagar en tiendas directamente desde tu cuenta bancaria - el dinero sale al instante, no es crédito.',
      'Interac e-Transfer es el metodo más popular para enviar dinero entre personas en Canadá. Solo necesitas el email del destinatario y el dinero llega en minutos.',
      'La mayoría de cuentas bancarias incluyen e-Transfer gratis. Algunas cuentas basicas cobran $1-2 por envió.',
    ],
    metaDescription:
      'Que es Interac en Canadá? Como funciona el débito y e-Transfer explicado en español. Guía para recién llegados latinos.',
    introduction:
      'Interac es el sistema de pagos que vas a usar todos los días en Canadá. Es como pagar con débito pero con la posibilidad de también enviar dinero a cualquier persona con un email. Para latinos, entender Interac es esencial desde el primer día.',
    sections: [
      {
        h2: '¿Cómo funciona Interac Debit para pagar en tiendas?',
        content:
          '<p>Cuando pagas con tu tarjeta de débito en Canadá, estas usando la red Interac. Es diferente a Visa o Mastercard - el dinero sale <strong>directamente de tu cuenta bancaria</strong> al instante.</p><p>Para pagar, simplemente acercas tu tarjeta (tap) o la insertas y pones tu PIN. La transacción se procesa en segundos y el monto se descuenta inmediatamente de tu saldo.</p><p>Puedes usar Interac Debit en prácticamente todas las tiendas de Canadá, desde supermercados hasta pequenos comercios.</p>',
      },
      {
        h2: '¿Cómo enviar dinero con Interac e-Transfer?',
        content:
          '<p>Interac e-Transfer es la forma más común de enviar dinero entre personas en Canadá:</p><ol><li>Abre la app de tu banco</li><li>Selecciona "Send Money" o "e-Transfer"</li><li>Ingresa el email o número de teléfono del destinatario</li><li>Ingresa el monto y un mensaje opcional</li><li>El destinatario recibe una notificacion y el dinero se deposita automáticamente (si tiene auto-deposit) o debe aceptar la transferencia</li></ol><p>El dinero generalmente llega en <strong>menos de 30 minutos</strong>. Es gratis con la mayoría de cuentas bancarias.</p>',
      },
      {
        h2: '¿Interac e-Transfer tiene límites?',
        content:
          '<p>Si, cada banco establece sus propios límites:</p><ul><li><strong>Límite por transacción:</strong> Típicamente $3,000-$10,000</li><li><strong>Límite diario:</strong> $3,000-$10,000</li><li><strong>Límite semanal:</strong> $10,000-$25,000</li></ul><p>Los límites varian por banco y tipo de cuenta. Cuentas premium generalmente tienen límites más altos.</p>',
      },
      {
        h2: '¿Es seguro usar Interac e-Transfer?',
        content:
          '<p>Si, Interac e-Transfer es muy seguro:</p><ul><li>Usa encriptacion bancaria de grado industrial</li><li>No comparte tu información bancaria con el destinatario</li><li>Ofrece la opción de pregunta de seguridad (aunque auto-deposit es más común ahora)</li></ul><p><strong>Consejo de seguridad:</strong> Activa auto-deposit en tu banco para que las transferencias se depositen automáticamente sin necesidad de pregunta de seguridad. Esto previene fraudes.</p>',
      },
    ],
    relatedTerms: ['sin-number-canada', 'credit-score-canada', 'secured-card-canada', 'cra-canada', 'credit-bureau-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Mejores cuentas con e-Transfer gratis',
      description: 'Compara cuentas bancarias que incluyen e-Transfer ilimitado y sin cargos mensuales.',
      ctaText: 'Comparar cuentas',
      ctaLink: '/banca',
    },
    faqs: [
      {
        question: '¿Cuánto cuesta enviar un Interac e-Transfer?',
        answer: 'La mayoría de cuentas chequing incluyen e-Transfer gratis. Algunas cuentas basicas cobran $1-2 por envió. Verifica con tu banco.',
      },
      {
        question: '¿Puedo enviar e-Transfer a alguien fuera de Canadá?',
        answer: 'No directamente. Interac e-Transfer funciona solo dentro de Canadá. Para envios internacionales, usa servicios como Wise, Remitly o Western Union.',
      },
      {
        question: '¿Qué es Interac Flash?',
        answer: 'Es la funcion de pago sin contacto (tap) de Interac. Puedes pagar acercando tu tarjeta de débito al terminal sin insertar ni poner PIN para compras menores a $250.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'basico',
    learningPath: ['sin-number-canada', 'credit-score-canada', 'secured-card-canada'],
    comparatorLinks: [{ label: 'Comparar cuentas bancarias', path: '/comparar-cuentas' }],
    sources: [
      { label: 'Interac - Official Website', url: 'https://www.interac.ca/en/' },
      { label: 'Interac - e-Transfer', url: 'https://www.interac.ca/en/consumers/products/interac-e-transfer/' },
    ],
  },
  {
    slug: 'credit-bureau-canada',
    term: 'Buros de Crédito',
    termFull: 'Equifax y TransUnion Canada',
    category: 'credito',
    difficulty: 'basico',
    readTime: 6,
    atomicAnswer:
      'Equifax y TransUnion son las dos agencias de crédito en Canadá que recopilan tu historial de pagos y calculan tu credit score. Los prestamistas consultan estas agencias al evaluarte para crédito, hipotecas y préstamos.',
    keyTakeaways: [
      'Solo existen dos buros de crédito en Canadá: Equifax y TransUnion. Tu puntaje puede ser ligeramente diferente en cada uno.',
      'Revisar tu propio score es un "soft check" que NO afecta tu puntaje. Solo las consultas de prestamistas ("hard checks") lo bajan temporalmente.',
      'Puedes obtener tu reporte de crédito gratis una vez al año directamente de cada buro, o usar Borrowell (Equifax) y Credit Karma (TransUnion).',
    ],
    metaDescription:
      'Equifax y TransUnion en Canadá explicados en español. Como revisar tu reporte de crédito gratis y que información contiene.',
    introduction:
      'Los buros de crédito son empresas privadas que recopilan información sobre tus creditos, pagos y deudas. Entender como funcionan te da poder sobre tu vida financiera en Canadá.',
    sections: [
      {
        h2: '¿Qué información tienen los buros de crédito sobre ti?',
        content:
          '<p>Tu reporte de crédito contiene:</p><ul><li><strong>Información personal:</strong> Nombre, dirección, SIN, fecha de nacimiento, empleador</li><li><strong>Cuentas de crédito:</strong> Tarjetas, lineas de crédito, préstamos, hipotecas</li><li><strong>Historial de pagos:</strong> Si pagaste a tiempo o tarde, montos pendientes</li><li><strong>Consultas:</strong> Quien ha revisado tu crédito y cuando</li><li><strong>Información publica:</strong> Bancarrota, juicios, cobros en coleccion</li></ul>',
      },
      {
        h2: '¿Cómo revisar tu reporte de crédito gratis?',
        content:
          '<p>Tienes varias opciones gratuitas:</p><ul><li><strong>Borrowell:</strong> Score de Equifax actualizado semanalmente, gratis</li><li><strong>Credit Karma:</strong> Score de TransUnion, también gratis</li><li><strong>Directamente de los buros:</strong> Puedes solicitar tu reporte completo una vez al año gratuitamente por correo</li></ul><p>Revisar tu propio score es un <strong>soft check</strong> que no afecta tu puntaje.</p>',
      },
      {
        h2: '¿Qué hacer si encuentras un error en tu reporte?',
        content:
          '<p>Los errores en reportes de crédito son más comunes de lo que piensas. Si encuentras uno:</p><ol><li>Documenta el error con evidencia (recibos, estados de cuenta)</li><li>Contacta al buro (Equifax o TransUnion) y presenta una disputa formal</li><li>Contacta al prestamista que reportó la información incorrecta</li><li>El buro tiene 30 días para investigar y responder</li></ol>',
      },
      {
        h2: '¿Cuál es la diferencia entre Equifax y TransUnion?',
        content:
          '<p>Ambos hacen lo mismo pero son empresas separadas:</p><ul><li>No todos los prestamistas reportan a ambos - por eso tu score puede variar</li><li>Cada uno usa su propio modelo de calculo</li><li>Algunos prestamistas consultan solo uno, otros consultan ambos</li></ul><p><strong>Recomendacion:</strong> Revisa tu score en ambos. Usa Borrowell para Equifax y Credit Karma para TransUnion.</p>',
      },
    ],
    relatedTerms: ['credit-score-canada', 'secured-card-canada', 'cra-canada', 'sin-number-canada', 'interac-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Revisa tu credit score gratis',
      description: 'Usa Borrowell para ver tu score de Equifax gratis, sin afectar tu puntaje.',
      ctaText: 'Ver mi score gratis',
      ctaLink: '/tarjetas-de-credito',
    },
    faqs: [
      {
        question: '¿Revisar mi score lo afecta negativamente?',
        answer: 'No. Revisarlo tu mismo es un "soft check" que no afecta tu puntaje. Solo las consultas de prestamistas ("hard checks") pueden bajarlo ligeramente y temporalmente.',
      },
      {
        question: '¿Cuánto tiempo permanece la información negativa en mi reporte?',
        answer: 'Los pagos atrasados permanecen 6 años. Una bancarrota permanece 6-7 años. Después de ese periodo, desaparecen automáticamente.',
      },
      {
        question: '¿Los buros de crédito canadienses comparten información con otros países?',
        answer: 'No. Tu reporte de crédito canadiense es independiente de cualquier otro país. Si te mudas fuera de Canadá, tu historial canadiense no te sigue.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['credit-score-canada', 'secured-card-canada', 'prime-rate-canada'],
    comparatorLinks: [],
    sources: [
      { label: 'Equifax Canada - Consumer', url: 'https://www.consumer.equifax.ca/personal/' },
      { label: 'TransUnion Canada', url: 'https://www.transunion.ca/' },
    ],
  },
  {
    slug: 'secured-card-canada',
    term: 'Tarjeta Asegurada',
    termFull: 'Secured Credit Card',
    category: 'credito',
    difficulty: 'basico',
    readTime: 6,
    atomicAnswer:
      'Una tarjeta de crédito asegurada requiere un depósito como garantia (típicamente $300-$500) y funciona identicamente a una tarjeta regular. Es la mejor herramienta para construir historial crediticio en Canadá desde cero, ideal para recién llegados.',
    keyTakeaways: [
      'Depositas una garantia ($300-$500) y recibes una tarjeta con ese límite. Funciona igual que una tarjeta normal en tiendas y en línea.',
      'Cada mes el banco reporta tus pagos a Equifax y TransUnion. Pagando a tiempo, tu credit score sube. En 6-12 meses muchos bancos te ofrecen una tarjeta regular.',
      'Cuando el banco te aprueba para una tarjeta no asegurada, te devuelven el depósito completo.',
    ],
    metaDescription:
      'Que es una tarjeta asegurada en Canadá? Como funciona, mejores opciones y cómo construir crédito desde cero como recién llegado.',
    introduction:
      'La tarjeta de crédito asegurada es el primer paso para construir tu vida financiera en Canadá. Es la única tarjeta que prácticamente cualquier persona puede obtener, sin importar si tienes historial crediticio o no.',
    sections: [
      {
        h2: '¿Cómo funciona una tarjeta de crédito asegurada?',
        content:
          '<p>El proceso es simple:</p><ol><li>Depositas una cantidad como garantia (mínimo $300, máximo $10,000 según el banco)</li><li>El banco te da una tarjeta de crédito con un límite igual a tu depósito</li><li>Usas la tarjeta normalmente - compras en tiendas, en línea, suscripciones</li><li>Pagas tu estado de cuenta cada mes (idealmente el saldo completo)</li><li>El banco reporta tus pagos a los buros de crédito</li></ol><p>Tu depósito NO se usa para pagar tus compras. Solo sirve como garantia para el banco en caso de que no pagues.</p>',
      },
      {
        h2: '¿Cuánto tiempo toma construir crédito con una secured card?',
        content:
          '<p>Con uso responsable:</p><ul><li><strong>3 meses:</strong> Empiezas a tener un score visible</li><li><strong>6 meses:</strong> Score típico de 650-680</li><li><strong>12 meses:</strong> Score de 700+, muchos bancos te ofrecen upgrade a tarjeta regular</li></ul><p><strong>Reglas para maximizar tu score:</strong></p><ul><li>Usa solo 20-30% de tu límite cada mes</li><li>Paga el saldo COMPLETO antes de la fecha límite</li><li>Nunca hagas solo el pago mínimo</li><li>Configura pagos automáticos para no olvidar</li></ul>',
      },
      {
        h2: '¿Cuáles son las mejores tarjetas aseguradas en Canadá?',
        content:
          '<p>Las opciones más populares para recién llegados:</p><ul><li><strong>Neo Secured:</strong> Sin cuota anual, cashback en compras</li><li><strong>Capital One Guaranteed Secured:</strong> Deposito mínimo bajo, buena app</li><li><strong>Home Trust Secured Visa:</strong> Aceptada ampliamente, depósito desde $500</li><li><strong>Refresh Financial Secured:</strong> Reporta a ambos buros</li></ul><p>Busca tarjetas que: (1) no cobren cuota anual, (2) reporten a ambos buros de crédito, y (3) tengan un proceso sencillo de graduacion a tarjeta regular.</p>',
      },
      {
        h2: '¿Cuándo recupero mi depósito de la tarjeta asegurada?',
        content:
          '<p>Recuperas tu depósito cuando:</p><ul><li><strong>El banco te gradua:</strong> Después de 6-12 meses de buen uso, muchos bancos automáticamente te ofrecen una tarjeta no asegurada y devuelven tu depósito</li><li><strong>Solicitas la graduacion:</strong> Si el banco no lo hace automáticamente, llama y solicita una revision</li><li><strong>Cierras la cuenta:</strong> Si decides cerrar la tarjeta, te devuelven el depósito menos cualquier saldo pendiente</li></ul><p><strong>Consejo:</strong> No cierres tu primera tarjeta después de graduarte. Mantenla abierta (sin usarla) para mantener la antiguedad de tu historial crediticio.</p>',
      },
    ],
    relatedTerms: ['credit-score-canada', 'credit-bureau-canada', 'interac-canada', 'sin-number-canada', 'cra-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Compara tarjetas aseguradas',
      description: 'Encuentra la mejor tarjeta asegurada para construir tu crédito en Canadá. Sin cuota anual y con cashback.',
      ctaText: 'Comparar tarjetas',
      ctaLink: '/tarjetas-de-credito',
    },
    faqs: [
      {
        question: '¿Puedo obtener una tarjeta asegurada sin SIN?',
        answer: 'La mayoría de bancos requieren SIN. Sin embargo, algunos bancos ofrecen opciones con ITIN o passport number para recién llegados que aun no tienen SIN.',
      },
      {
        question: '¿La tarjeta asegurada cobra intereses?',
        answer: 'Si, como cualquier tarjeta de crédito. Si no pagas el saldo completo antes de la fecha límite, te cobran intereses (típicamente 19.99% anual). Por eso es crucial pagar el saldo completo cada mes.',
      },
      {
        question: '¿Puedo aumentar el límite de mi tarjeta asegurada?',
        answer: 'Si, depositando más dinero como garantia adicional. La mayoría de bancos permiten aumentar tu depósito y por ende tu límite.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['credit-score-canada', 'credit-bureau-canada', 'prime-rate-canada'],
    comparatorLinks: [{ label: 'Comparar tarjetas de crédito', path: '/tarjetas-de-credito' }],
    sources: [
      { label: 'Financial Consumer Agency of Canada - Credit Cards', url: 'https://www.canada.ca/en/financial-consumer-agency/services/credit-cards.html' },
      { label: 'Government of Canada - Building Credit', url: 'https://www.canada.ca/en/financial-consumer-agency/services/credit-reports-score/build-credit.html' },
    ],
  },
  {
    slug: 'cra-canada',
    term: 'CRA',
    termFull: 'Canada Revenue Agency',
    category: 'impuestos',
    difficulty: 'basico',
    readTime: 7,
    atomicAnswer:
      'La CRA (Canada Revenue Agency) es la agencia federal de impuestos de Canadá - el equivalente al SAT, DIAN o SUNAT. Administra la recaudacion fiscal, beneficios como el GST Credit y Child Benefit, y tu cuenta tributaria personal.',
    keyTakeaways: [
      'La CRA recauda impuestos federales, administra beneficios sociales (GST Credit, Child Benefit) y supervisa tu cumplimiento fiscal.',
      'CRA My Account es tu portal en línea donde ves tus T4, NOA, límites de RRSP/TFSA, saldo de impuestos y estado de beneficios.',
      'La CRA ofrece servicio en ingles y frances. No hay servicio oficial en español, pero puedes pedir un interprete al llamar.',
    ],
    metaDescription:
      'Que es la CRA en Canadá? Agencia de impuestos explicada en español. CRA My Account, impuestos, beneficios y como contactarla.',
    introduction:
      'La CRA es la entidad más importante en tu vida financiera en Canadá. Administra tus impuestos, distribuye beneficios y mantiene tu historial fiscal. Entender como funciona te ahorra dinero y problemas.',
    sections: [
      {
        h2: '¿Qué hace la CRA y por que es importante?',
        content:
          '<p>La CRA tiene varias funciones clave:</p><ul><li><strong>Recaudar impuestos:</strong> Procesa tu declaración de impuestos anual (T1)</li><li><strong>Distribuir beneficios:</strong> GST/HST Credit, Canada Child Benefit, Canadá Workers Benefit</li><li><strong>Administrar cuentas registradas:</strong> Controla tus límites de TFSA, RRSP, FHSA</li><li><strong>Fiscalizar:</strong> Puede auditar tus declaraciones y cobrar impuestos adeudados</li></ul>',
      },
      {
        h2: '¿Cómo registrarse en CRA My Account?',
        content:
          '<p>CRA My Account es tu portal personal. Para registrarte necesitas:</p><ol><li>Visita <strong>canada.ca</strong> y busca "CRA My Account"</li><li>Necesitas tu SIN y un T4 o NOA anterior para verificar tu identidad</li><li>Crea un usuario y contrasena</li><li>Una vez dentro, puedes ver tus T4, NOA, límites de RRSP/TFSA, estado de beneficios y saldo de impuestos</li></ol><p>También puedes usar tu banco como acceso (sign-in partner) - la mayoría de bancos canadienses ofrecen esta opción.</p>',
      },
      {
        h2: '¿Qué beneficios administra la CRA?',
        content:
          '<p>Los principales beneficios que la CRA distribuye:</p><ul><li><strong>GST/HST Credit:</strong> Pago trimestral para personas de ingresos bajos/moderados</li><li><strong>Canada Child Benefit:</strong> Pago mensual por cada hijo menor de 18</li><li><strong>Canadá Workers Benefit:</strong> Crédito para trabajadores de bajos ingresos</li><li><strong>Climate Action Incentive:</strong> Reembolso por carbon tax en provincias aplicables</li></ul><p>Para recibir estos beneficios, <strong>DEBES declarar impuestos cada año</strong>, incluso si no ganaste dinero.</p>',
      },
      {
        h2: '¿Cómo contactar a la CRA?',
        content:
          '<p>Opciones de contacto:</p><ul><li><strong>Teléfono:</strong> 1-800-959-8281 (individuos) - horario L-V 9am-5pm hora local</li><li><strong>En línea:</strong> CRA My Account para consultas y cambios</li><li><strong>Correo:</strong> Puedes enviar documentos por correo certificado</li></ul><p><strong>Nota para hispanohablantes:</strong> La CRA opera en ingles y frances. No hay línea en español, pero puedes pedir un servicio de interpretacion telefonica al llamar.</p>',
      },
    ],
    relatedTerms: ['t4-canada', 'gst-hst-credit', 'child-benefit-canada', 'revenu-quebec-canada', 'sin-number-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Declara tus impuestos fácilmente',
      description: 'Usa software gratuito para declarar tus impuestos ante la CRA sin complicaciones.',
      ctaText: 'Ver opciones gratuitas',
      ctaLink: '/herramientas',
    },
    faqs: [
      {
        question: '¿Puedo llamar a la CRA en español?',
        answer: 'La CRA ofrece servicio en ingles y frances unicamente. Sin embargo, puedes solicitar un interprete al llamar al 1-800-959-8281.',
      },
      {
        question: '¿Qué pasa si no declaro impuestos?',
        answer: 'Pierdes acceso a beneficios como GST Credit y Child Benefit. Además, la CRA puede cobrar multas e intereses sobre impuestos adeudados.',
      },
      {
        question: '¿La CRA puede auditar mi declaración?',
        answer: 'Si. La CRA selecciona declaraciones para revision cada año. Guarda todos tus recibos y documentos fiscales por al menos 6 años.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['t4-canada', 'gst-hst-credit', 'child-benefit-canada'],
    comparatorLinks: [],
    sources: [
      { label: 'Canada Revenue Agency - Official', url: 'https://www.canada.ca/en/revenue-agency.html' },
      { label: 'CRA - My Account for Individuals', url: 'https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-individuals/account-individuals.html' },
    ],
  },
  {
    slug: 'revenu-quebec-canada',
    term: 'Revenu Quebec',
    termFull: 'Agencia de Impuestos Provincial de Quebec',
    category: 'impuestos',
    difficulty: 'intermedio',
    readTime: 6,
    atomicAnswer:
      'Revenu Quebec es la agencia de impuestos provincial de Quebec. Si vives en Quebec, debes declarar impuestos tanto ante la CRA (federal) como ante Revenu Quebec (provincial) - dos declaraciones separadas cada año.',
    keyTakeaways: [
      'Quebec es la única provincia que administra sus propios impuestos. Debes hacer dos declaraciones: T1 (CRA) y TP-1 (Revenu Quebec).',
      'Administra el Solidarity Tax Credit, el QPP (Quebec Pension Plan), y creditos por guarderia que no existen en otras provincias.',
      'Software como Wealthsimple Tax genera ambas declaraciones automáticamente.',
    ],
    metaDescription:
      'Que es Revenu Quebec? Impuestos provinciales en Quebec explicados en español. Dos declaraciones, Solidarity Tax Credit y QPP.',
    introduction:
      'Si vives en Quebec, tu vida fiscal es un poco más compleja que en el resto de Canadá. Revenu Quebec administra sus propios impuestos provinciales, lo que significa que debes hacer dos declaraciones cada año.',
    sections: [
      {
        h2: '¿Por qué Quebec tiene su propia agencia de impuestos?',
        content:
          '<p>Quebec es la única provincia de Canadá que recauda sus propios impuestos provinciales. Esto es resultado de acuerdos historicos entre Quebec y el gobierno federal. En la practica significa que:</p><ul><li>Debes presentar dos declaraciones: <strong>T1</strong> (federal, ante la CRA) y <strong>TP-1</strong> (provincial, ante Revenu Quebec)</li><li>Recibes dos avisos de evaluación separados</li><li>Puedes deber a uno y recibir reembolso del otro</li><li>Quebec tiene tasas de impuestos provinciales propias (generalmente más altas)</li></ul>',
      },
      {
        h2: '¿Qué beneficios exclusivos ofrece Revenu Quebec?',
        content:
          '<p>Vivir en Quebec tiene ventajas fiscales unicas:</p><ul><li><strong>Solidarity Tax Credit:</strong> Combina creditos por QST, vivienda y municipios nordicos - puede sumar $1,200+/ano</li><li><strong>Guarderias a $9.10/dia:</strong> Las plazas subvencionadas son significativamente más baratas que en el resto de Canadá</li><li><strong>Crédito fiscal por guarderia:</strong> Si usas guarderia privada, el crédito provincial reduce significativamente el costo</li><li><strong>QPP (Quebec Pension Plan):</strong> Plan de pensiones propio que reemplaza al CPP federal</li></ul>',
      },
      {
        h2: '¿Cómo declarar impuestos en Quebec?',
        content:
          '<p>El proceso es similar al del resto de Canadá, pero duplicado:</p><ol><li>Recopila tus documentos: T4, Releve 1 (equivalente provincial del T4), recibos de gastos</li><li>Usa software de impuestos - Wealthsimple Tax genera ambas declaraciones automáticamente</li><li>Presenta la T1 ante la CRA y la TP-1 ante Revenu Quebec</li><li>Ambas tienen fecha límite el 30 de abril</li></ol>',
      },
      {
        h2: '¿Qué es el Releve 1 y en que se diferencia del T4?',
        content:
          '<p>El <strong>Releve 1 (RL-1)</strong> es el equivalente provincial del T4 en Quebec:</p><ul><li><strong>T4:</strong> Lo emite tu empleador para la CRA (federal) - reporta impuestos federales, CPP, EI</li><li><strong>RL-1:</strong> Lo emite tu empleador para Revenu Quebec - reporta impuestos provinciales, QPP, QPIP</li></ul><p>Necesitas AMBOS documentos para hacer tus dos declaraciones. Tu empleador debe entregarte ambos antes del 28 de febrero.</p>',
      },
    ],
    relatedTerms: ['cra-canada', 't4-canada', 'gst-hst-credit', 'sin-number-canada', 'rrsp-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Declara impuestos en Quebec gratis',
      description: 'Wealthsimple Tax genera ambas declaraciones (federal y provincial) automáticamente y gratis.',
      ctaText: 'Declarar gratis',
      ctaLink: '/herramientas',
    },
    faqs: [
      {
        question: '¿Necesito dos contadores si vivo en Quebec?',
        answer: 'No necesariamente. La mayoría del software de impuestos genera ambas declaraciones automáticamente. Un contador que trabaje en Quebec manejara ambas.',
      },
      {
        question: '¿Es más caro vivir en Quebec por los impuestos?',
        answer: 'Las tasas provinciales son más altas, pero Quebec ofrece más servicios: guarderías a $9.10/dia, universidad más barata, seguro de medicamentos universal y el Solidarity Tax Credit.',
      },
      {
        question: '¿Puedo contactar a Revenu Quebec en español?',
        answer: 'Revenu Quebec opera en frances e ingles. No hay servicio en español, pero puedes solicitar asistencia en ingles.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['cra-canada', 't4-canada', 'gst-hst-credit'],
    comparatorLinks: [],
    sources: [
      { label: 'Revenu Quebec - Official', url: 'https://www.revenuquebec.ca/en/' },
      { label: 'Government of Quebec - Tax Credits', url: 'https://www.revenuquebec.ca/en/citizens/tax-credits/' },
    ],
  },
  {
    slug: 'child-benefit-canada',
    term: 'Canada Child Benefit',
    termFull: 'Beneficio Canadiense para Hijos (CCB)',
    category: 'vida',
    difficulty: 'basico',
    readTime: 6,
    atomicAnswer:
      'El Canada Child Benefit (CCB) es un pago mensual libre de impuestos para familias con hijos menores de 18 años. Puede ser hasta $7,787/ano por hijo menor de 6 y $6,570 para hijos de 6-17. El monto depende de tu ingreso familiar.',
    keyTakeaways: [
      'Pago mensual libre de impuestos: hasta $7,787/ano por hijo menor de 6 y $6,570 para hijos de 6-17 (2025-2026).',
      'Para recibirlo DEBES declarar impuestos cada año - ambos padres si aplica. Se calcula automáticamente basado en tu ingreso.',
      'Personas con residencia permanente, permiso de trabajo o estatus de refugiado califican si son residentes fiscales.',
    ],
    metaDescription:
      'Que es el Canada Child Benefit? Pago mensual por hijos explicado en español. Cuanto recibes, como aplicar y requisitos para recién llegados.',
    introduction:
      'El Canada Child Benefit es uno de los beneficios más generosos para familias en Canadá. El gobierno te paga cada mes por cada hijo menor de 18 años, y el dinero es libre de impuestos.',
    sections: [
      {
        h2: '¿Cuánto dinero recibo del Canada Child Benefit?',
        content:
          '<p>Los montos máximos para el periodo julio 2025 a junio 2026 son:</p><ul><li><strong>Hijos menores de 6:</strong> hasta $7,787/ano ($648.91/mes)</li><li><strong>Hijos de 6-17:</strong> hasta $6,570/ano ($547.50/mes)</li></ul><p>El monto se reduce gradualmente a medida que tu ingreso familiar neto supera ~$36,502. Familias con ingresos superiores a ~$75,000 reciben montos reducidos.</p><p><strong>Ejemplo:</strong> Una familia con 2 hijos menores de 6 y un ingreso familiar de $50,000 podria recibir aproximadamente $1,000/mes libre de impuestos.</p>',
      },
      {
        h2: '¿Cómo aplicar al Canada Child Benefit?',
        content:
          '<p>Hay varias formas de aplicar:</p><ul><li><strong>Al registrar el nacimiento:</strong> Si tu hijo nace en Canadá, puedes aplicar al CCB en el formulario de registro de nacimiento provincial</li><li><strong>A traves de CRA My Account:</strong> Seccion "Apply for child benefits"</li><li><strong>Formulario RC66:</strong> Disponible en canada.ca, se puede enviar por correo</li></ul><p><strong>Requisito clave:</strong> Ambos padres (si aplica) DEBEN declarar impuestos cada año para seguir recibiendo el beneficio.</p>',
      },
      {
        h2: '¿Los recién llegados pueden recibir el CCB?',
        content:
          '<p>Si, si cumples estos requisitos:</p><ul><li>Eres residente de Canadá (residencia permanente, permiso de trabajo, refugiado)</li><li>Tienes un SIN valido</li><li>Vives con el hijo y eres su cuidador principal</li><li>Declaras impuestos en Canadá</li></ul><p><strong>Para recién llegados:</strong> Aplica lo antes posible al llegar. Puedes recibir retroactivo hasta 11 meses.</p>',
      },
      {
        h2: '¿Cómo maximizar tu Canada Child Benefit?',
        content:
          '<p>Estrategias legales para recibir más CCB:</p><ul><li><strong>Contribuye al RRSP:</strong> Reduces tu ingreso neto, lo que puede aumentar tu CCB</li><li><strong>Declara siempre:</strong> Incluso si uno de los padres no trabajo, debe declarar para que la CRA calcule el beneficio correctamente</li><li><strong>Actualiza tu situación:</strong> Si cambia tu estado civil, número de hijos o dirección, notifica a la CRA inmediatamente</li></ul>',
      },
    ],
    relatedTerms: ['cra-canada', 'gst-hst-credit', 'sin-number-canada', 'rrsp-canada', 't4-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Calcula tu CCB estimado',
      description: 'Usa la calculadora oficial de la CRA para estimar cuanto recibirías de Child Benefit.',
      ctaText: 'Calcular beneficio',
      ctaLink: '/herramientas',
    },
    faqs: [
      {
        question: '¿El CCB cuenta como ingreso para impuestos?',
        answer: 'No. El Canada Child Benefit es 100% libre de impuestos. No se reporta como ingreso y no afecta tu declaración.',
      },
      {
        question: '¿Qué pasa si me separo o divorcio?',
        answer: 'El CCB se paga al cuidador principal del hijo. Si cambia la custodia, debes notificar a la CRA para que ajuste los pagos.',
      },
      {
        question: '¿Puedo recibir CCB retroactivo?',
        answer: 'Si, hasta 11 meses hacia atras desde la fecha de tu solicitud. Por eso es importante aplicar lo antes posible.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'alto',
    learningPath: ['gst-hst-credit', 'cra-canada', 'tfsa-canada'],
    comparatorLinks: [],
    sources: [
      { label: 'CRA - Canada Child Benefit', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/canada-child-benefit-overview.html' },
      { label: 'CRA - CCB Calculator', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/canada-child-benefit-overview/canada-child-benefit-we-calculate-your-ccb.html' },
    ],
  },
  {
    slug: 'gst-hst-credit',
    term: 'GST/HST Credit',
    termFull: 'Crédito por Impuesto sobre Bienes y Servicios',
    category: 'impuestos',
    difficulty: 'basico',
    readTime: 5,
    atomicAnswer:
      'El GST/HST Credit es un pago trimestral libre de impuestos para personas y familias de ingresos bajos a moderados. Compensa el impuesto sobre ventas que pagas en compras diarias. Se recibe automáticamente al declarar impuestos.',
    keyTakeaways: [
      'Pago trimestral automático (enero, abril, julio, octubre) libre de impuestos para compensar el GST/HST en compras diarias.',
      'Una persona soltera puede recibir hasta ~$519/ano. Una familia recibe más dependiendo del número de hijos.',
      'Solo necesitas declarar impuestos cada año para recibirlo. La CRA calcula automáticamente si calificas.',
    ],
    metaDescription:
      'Que es el GST/HST Credit en Canadá? Pago trimestral explicado en español. Cuanto recibes, fechas de pago y como calificar.',
    introduction:
      'El GST/HST Credit es dinero gratis del gobierno para compensar los impuestos de ventas que pagas todos los días. Solo necesitas declarar impuestos para recibirlo automáticamente.',
    sections: [
      {
        h2: '¿Cuánto recibo de GST/HST Credit?',
        content:
          '<p>Para el periodo julio 2025 a junio 2026:</p><ul><li><strong>Persona soltera:</strong> hasta ~$519/ano</li><li><strong>Pareja:</strong> hasta ~$680/ano</li><li><strong>Por cada hijo menor de 19:</strong> ~$179 adicionales</li></ul><p>El monto se reduce gradualmente si tu ingreso neto supera ~$44,000 (soltera) o ~$58,000 (familia). Se paga en 4 cuotas trimestrales.</p>',
      },
      {
        h2: '¿Cuándo se paga el GST/HST Credit?',
        content:
          '<p>Las fechas de pago para 2026 son:</p><ul><li><strong>5 de enero 2026</strong></li><li><strong>5 de abril 2026</strong></li><li><strong>5 de julio 2026</strong></li><li><strong>5 de octubre 2026</strong></li></ul><p>El pago se deposita automáticamente en tu cuenta bancaria si tienes depósito directo configurado con la CRA.</p>',
      },
      {
        h2: '¿Cómo calificar para el GST/HST Credit?',
        content:
          '<p>Para calificar necesitas:</p><ul><li>Ser residente de Canadá</li><li>Tener 19 años o más (o tener pareja o ser padre)</li><li><strong>Declarar impuestos cada año</strong> - este es el único requisito activo</li></ul><p>No necesitas aplicar por separado. La CRA calcula automáticamente si calificas basándose en tu declaración de impuestos.</p>',
      },
      {
        h2: '¿Qué hago si no recibi mi GST/HST Credit?',
        content:
          '<p>Si no recibiste tu pago:</p><ol><li>Verifica que declaraste impuestos del año anterior</li><li>Revisa tu información de depósito directo en CRA My Account</li><li>Si tu pareja también debe declarar, asegurate de que ambos lo hicieron</li><li>Contacta a la CRA al 1-800-959-1953</li></ol>',
      },
    ],
    relatedTerms: ['cra-canada', 'child-benefit-canada', 't4-canada', 'sin-number-canada', 'revenu-quebec-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Declara tus impuestos para recibir tu crédito',
      description: 'Declarar impuestos es el único requisito. Hazlo gratis con Wealthsimple Tax.',
      ctaText: 'Declarar gratis',
      ctaLink: '/herramientas',
    },
    faqs: [
      {
        question: '¿Recibo GST Credit si no trabajé este año?',
        answer: 'Si, siempre que declares impuestos. Incluso con ingreso $0, debes declarar para que la CRA te evalúe.',
      },
      {
        question: '¿El GST Credit afecta otros beneficios?',
        answer: 'No. El GST/HST Credit es libre de impuestos y no se cuenta como ingreso para calcular otros beneficios.',
      },
      {
        question: '¿Puedo recibir GST Credit y Solidarity Tax Credit al mismo tiempo?',
        answer: 'Si vives en Quebec, recibes el Solidarity Tax Credit (que incluye un componente QST) de Revenu Quebec, más el GST Credit federal de la CRA. Son beneficios separados.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['cra-canada', 'child-benefit-canada', 't4-canada'],
    comparatorLinks: [],
    sources: [
      { label: 'CRA - GST/HST Credit', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/goods-services-tax-harmonized-sales-tax-gst-hst-credit.html' },
      { label: 'CRA - Payment Dates', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/goods-services-tax-harmonized-sales-tax-gst-hst-credit/gst-hst-credit-payment-dates.html' },
    ],
  },
  {
    slug: 'prime-rate-canada',
    term: 'Prime Rate',
    termFull: 'Tasa Preferencial Bancaria de Canadá',
    category: 'credito',
    difficulty: 'intermedio',
    readTime: 6,
    atomicAnswer:
      'El prime rate es la tasa de interés de referencia que los bancos canadienses usan para fijar precios de hipotecas variables, lineas de crédito y préstamos. Cuando el Banco de Canadá sube su tasa de política, el prime rate sube también.',
    keyTakeaways: [
      'El prime rate en marzo 2026 es 4.45% (tasa directriz del Banco de Canadá: 2.25%). Las hipotecas variables y lineas de crédito se expresan como "prime +/- X%".',
      'Cuando el Banco de Canadá sube o baja su tasa de política monetaria, los bancos ajustan su prime rate en la misma dirección.',
      'Una tasa variable "prime - 0.50%" significa que pagas 3.95% si el prime es 4.45%. Si el prime sube a 4.70%, tu tasa sube a 4.20%.',
    ],
    metaDescription:
      'Que es el prime rate en Canadá? Como afecta tu hipoteca y crédito. Tasa actual, historico y que esperar en 2026.',
    introduction:
      'El prime rate es el número que determina cuanto pagas por tu hipoteca variable, línea de crédito y muchos préstamos en Canadá. Entender como funciona te ayuda a tomar mejores decisiones financieras.',
    sections: [
      {
        h2: '¿Cómo funciona el prime rate en Canadá?',
        content:
          '<p>El prime rate es la tasa de referencia que los bancos comerciales usan como base para sus productos de crédito. No es una tasa del gobierno - cada banco establece su propio prime rate, aunque en la practica todos los grandes bancos (TD, RBC, Scotiabank, BMO, CIBC) mantienen el mismo nivel.</p><p>Los productos afectados por el prime rate incluyen:</p><ul><li>Hipotecas de tasa variable</li><li>Lineas de crédito personales (LOC)</li><li>Lineas de crédito con garantia hipotecaria (HELOC)</li><li>Algunos préstamos estudiantiles</li></ul>',
      },
      {
        h2: '¿Cómo afecta el prime rate a mi hipoteca?',
        content:
          '<p>Si tienes una hipoteca variable, tu tasa se calcula como prime +/- un diferencial:</p><p><strong>Ejemplo:</strong> Si tu hipoteca es "prime - 0.70%" y el prime es 4.45%, tu tasa efectiva es 3.75%. Si el Banco de Canadá sube su tasa 0.25%, el prime sube a 4.70% y tu tasa sube a 4.00%.</p><p>Con una hipoteca fija, el prime rate no te afecta durante tu term (típicamente 5 años). Solo importa cuando renueves.</p>',
      },
      {
        h2: '¿Es mejor tasa fija o variable para mi hipoteca?',
        content:
          '<p>No hay respuesta universal, pero considera:</p><ul><li><strong>Variable:</strong> Historicamente más barata a largo plazo. Buena si toleras fluctuaciones y planeas quedarte más de 5 años</li><li><strong>Fija:</strong> Certeza total en tus pagos. Ideal si eres nuevo en Canadá y prefieres estabilidad</li></ul><p>Muchos asesores recomiendan variable si la diferencia entre fija y variable es mayor a 0.5%. Si la diferencia es minima, la fija ofrece tranquilidad.</p>',
      },
      {
        h2: '¿Qué esperar del prime rate en 2026?',
        content:
          '<p>El prime rate depende directamente de las decisiones del <strong>Banco de Canadá</strong>, que se reune 8 veces al año para evaluar la tasa de política monetaria.</p><p>Factores que influyen:</p><ul><li><strong>Inflacion:</strong> Si la inflación sube, el Banco de Canadá sube tasas</li><li><strong>Empleo:</strong> Desempleo alto puede llevar a bajar tasas</li><li><strong>Economia global:</strong> Recesiones o crisis externas pueden forzar cambios</li></ul><p>Puedes seguir los anuncios del Banco de Canadá en bankofcanada.ca para anticipar cambios.</p>',
      },
    ],
    relatedTerms: ['amortización-canada', 'credit-score-canada', 'cra-canada', 'secured-card-canada', 'credit-bureau-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Compara tasas hipotecarias actuales',
      description: 'Ve las mejores tasas fijas y variables disponibles para compradores en Canadá.',
      ctaText: 'Ver tasas',
      ctaLink: '/hipotecas',
    },
    faqs: [
      {
        question: '¿Cada cuánto cambia el prime rate?',
        answer: 'El Banco de Canadá hace anuncios de tasa 8 veces al año. El prime rate puede cambiar en cada anuncio, pero no siempre lo hace.',
      },
      {
        question: '¿El prime rate afecta mi tarjeta de crédito?',
        answer: 'Generalmente no directamente. Las tasas de interés de tarjetas de crédito (típicamente 19.99%) son fijas y no cambian con el prime rate.',
      },
      {
        question: '¿Puedo negociar una mejor tasa que prime?',
        answer: 'Si. Los brokers hipotecarios frecuentemente consiguen tasas de "prime - X%" que son mejores que lo que el banco ofrece directamente.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['amortización-canada', 'cmhc', 'fhsa-canada'],
    comparatorLinks: [{ label: 'Comparar hipotecas', path: '/comparar-hipotecas' }],
    sources: [
      { label: 'Bank of Canada - Policy Interest Rate', url: 'https://www.bankofcanada.ca/core-functions/monetary-policy/key-interest-rate/' },
      { label: 'Financial Consumer Agency - Interest Rates', url: 'https://www.canada.ca/en/financial-consumer-agency/services/mortgages/interest-rates.html' },
    ],
  },
  {
    slug: 'amortización-canada',
    term: 'Amortizacion',
    termFull: 'Periodo de Amortizacion Hipotecaria',
    category: 'vivienda',
    difficulty: 'intermedio',
    readTime: 6,
    atomicAnswer:
      'La amortización es el periodo total en años para pagar tu hipoteca completamente. En Canadá, el máximo estandar es 25 años (30 años para casas nuevas desde 2024). Una amortización más larga reduce el pago mensual pero aumenta significativamente el interés total.',
    keyTakeaways: [
      'El periodo estandar es 25 años. Desde 2024, compradores de casas nuevas pueden elegir 30 años, lo que reduce el pago mensual ~10-15%.',
      'No confundir amortización con "term": el term es el periodo de tu contrato hipotecario (típicamente 5 años), la amortización es el plazo total de pago.',
      'Puedes hacer pagos adicionales (prepayments) de 10-20% anual sin penalización para pagar tu hipoteca más rápido.',
    ],
    metaDescription:
      'Que es la amortización hipotecaria en Canadá? 25 vs 30 años, como afecta tu pago mensual y estrategias para pagar más rápido.',
    introduction:
      'Cuando compras una casa en Canadá, la amortización determina en cuántos años pagarás tu hipoteca por completo. Es una de las decisiones financieras más importantes porque afecta tanto tu pago mensual como el total de intereses que pagarás.',
    sections: [
      {
        h2: '¿Qué es la amortización y como se diferencia del term?',
        content:
          '<p>Es crucial entender la diferencia:</p><ul><li><strong>Amortizacion:</strong> El periodo TOTAL para pagar la hipoteca (ej. 25 años)</li><li><strong>Term:</strong> El periodo de tu contrato actual con el banco (típicamente 5 años)</li></ul><p>Cada 5 años (al terminar tu term), renegocias tu tasa de interés con tu banco actual o cambias a otro banco. Pero la amortización restante continua.</p><p><strong>Ejemplo:</strong> Tienes una hipoteca con amortización de 25 años y term de 5 años. Después de 5 años, te quedan 20 años de amortización. Renegocias la tasa para los proximos 5 años.</p>',
      },
      {
        h2: '25 años vs 30 años - ¿cuál conviene?',
        content:
          '<p>Desde 2024, compradores de casas nuevas pueden elegir 30 años:</p><table><tr><th>Hipoteca $500,000 al 5%</th><th>25 años</th><th>30 años</th></tr><tr><td>Pago mensual</td><td>$2,908</td><td>$2,684</td></tr><tr><td>Ahorro mensual</td><td>-</td><td>$224/mes</td></tr><tr><td>Interes total</td><td>$372,400</td><td>$466,240</td></tr><tr><td>Costo extra</td><td>-</td><td>$93,840 más</td></tr></table><p>30 años reduce tu pago mensual pero cuesta casi $94,000 más en intereses sobre la vida de la hipoteca.</p>',
      },
      {
        h2: '¿Cómo pagar mi hipoteca más rápido?',
        content:
          '<p>Estrategias legales para reducir tu amortización:</p><ul><li><strong>Prepayments:</strong> La mayoría de hipotecas permite pagos adicionales de 10-20% del monto original por año sin penalización</li><li><strong>Pagos quincenales acelerados:</strong> En vez de pagar mensual, paga cada 2 semanas. Esto resulta en un pago extra al año</li><li><strong>Aumentar el monto del pago:</strong> Muchos bancos permiten aumentar tu pago regular hasta un 20%</li><li><strong>Al renovar:</strong> Elige un term más corto si las tasas son favorables</li></ul>',
      },
      {
        h2: '¿Necesito seguro CMHC si mi amortización es de 30 años?',
        content:
          '<p>Si tu enganche es menor al 20%, necesitas seguro hipotecario de CMHC sin importar la amortización. El costo del seguro varia:</p><ul><li>5% enganche: 4.00% del monto de hipoteca</li><li>10% enganche: 3.10%</li><li>15% enganche: 2.80%</li></ul><p>El seguro se suma al monto de tu hipoteca. Para una casa de $500,000 con 5% de enganche ($25,000), el seguro CMHC seria aproximadamente $19,000.</p>',
      },
    ],
    relatedTerms: ['prime-rate-canada', 'fhsa-canada', 'credit-score-canada', 'cra-canada', 'rrsp-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Calcula tu pago hipotecario',
      description: 'Usa nuestra calculadora para ver cómo la amortización afecta tu pago mensual.',
      ctaText: 'Calcular pago',
      ctaLink: '/calculadora-hipoteca',
    },
    faqs: [
      {
        question: '¿Puedo cambiar mi amortización después de firmar?',
        answer: 'Si, al renovar tu term (típicamente cada 5 años). Puedes elegir una amortización más corta para pagar más rápido.',
      },
      {
        question: '¿Hay penalización por pagar mi hipoteca antes?',
        answer: 'Los prepayments dentro del límite permitido (10-20% anual) no tienen penalización. Si quieres pagar la hipoteca completa antes del term, puede haber una penalización que varia según el tipo de tasa.',
      },
      {
        question: '¿Qué amortización recomiendan para recién llegados?',
        answer: '25 años es la opción más común y equilibrada. Si tu presupuesto es ajustado, 30 años (para casas nuevas) te da más flexibilidad mensual.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-20',
    impact: 'medio',
    learningPath: ['cmhc', 'prime-rate-canada', 'fhsa-canada'],
    comparatorLinks: [{ label: 'Calculadora hipotecaria', path: '/calculadora-hipoteca' }, { label: 'Comparar hipotecas', path: '/comparar-hipotecas' }],
    sources: [
      { label: 'CMHC - Mortgage Insurance', url: 'https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance' },
      { label: 'Financial Consumer Agency - Mortgages', url: 'https://www.canada.ca/en/financial-consumer-agency/services/mortgages.html' },
    ],
  },
  {
    slug: 'cmhc',
    term: 'CMHC',
    termFull: 'Canada Mortgage and Housing Corporation',
    category: 'vivienda',
    difficulty: 'intermedio',
    readTime: 7,
    atomicAnswer:
      'La CMHC (Canada Mortgage and Housing Corporation) es la agencia federal que administra el seguro hipotecario obligatorio cuando tu enganche es menor al 20%. Este seguro protege al banco, pero lo pagas tu: la prima va del 2.8% al 4.0% del monto hipotecado, y se suma al saldo de tu hipoteca.',
    keyTakeaways: [
      'Si tu enganche es menor al 20% del precio de la vivienda, el seguro CMHC es obligatorio por ley en Canadá.',
      'La prima varia según tu porcentaje de enganche: 4.0% (enganche del 5-9.99%), 3.1% (10-14.99%), 2.8% (15-19.99%).',
      'La prima se agrega al monto de tu hipoteca - no la pagas de tu bolsillo, pero si pagas intereses sobre ella durante toda la amortización.',
    ],
    metaDescription:
      'Que es el seguro CMHC en Canadá? Seguro hipotecario obligatorio explicado en español. Primas 2026, como se calcula, requisitos y alternativas.',
    introduction:
      'Si estas pensando en comprar casa en Canadá con un enganche menor al 20%, necesitas conocer el seguro CMHC. Es un costo significativo que muchos compradores primerizos - especialmente recién llegados - no anticipan. Entender como funciona te ayudará a planificar mejor tu presupuesto y decidir cuánto enganche conviene ahorrar.',
    sections: [
      {
        h2: '¿Qué es el seguro hipotecario CMHC?',
        content:
          '<p>El seguro hipotecario de la CMHC (también llamado mortgage default insurance) es una proteccion que los bancos exigen cuando el comprador aporta menos del 20% del precio de la vivienda como enganche.</p><p><strong>Punto clave:</strong> el seguro protege al <strong>banco</strong>, no a ti. Si dejas de pagar tu hipoteca, la CMHC le reembolsa al banco. Pero el costo de la prima lo paga el comprador.</p><p>En Canadá hay tres aseguradoras hipotecarias autorizadas: <strong>CMHC</strong> (la más conocida, es del gobierno federal), <strong>Sagen</strong> (antes Genworth) y <strong>Canada Guaranty</strong>. Las tres cobran primas similares.</p>',
      },
      {
        h2: '¿Cuánto cuesta el seguro CMHC?',
        content:
          '<p>La prima depende del porcentaje de tu enganche respecto al precio de compra:</p><table><tr><th>Enganche</th><th>Prima CMHC</th><th>Ejemplo (casa de $500,000)</th></tr><tr><td>5% – 9.99%</td><td>4.00%</td><td>$19,000 sobre hipoteca de $475,000</td></tr><tr><td>10% – 14.99%</td><td>3.10%</td><td>$13,950 sobre hipoteca de $450,000</td></tr><tr><td>15% – 19.99%</td><td>2.80%</td><td>$11,900 sobre hipoteca de $425,000</td></tr><tr><td>20% o más</td><td>0% (no se requiere)</td><td>Sin prima</td></tr></table><p><strong>Importante:</strong> la prima se suma al saldo de tu hipoteca. En el ejemplo de 5% de enganche, tu hipoteca pasa de $475,000 a $494,000. Pagas intereses sobre esos $19,000 adicionales durante toda la amortización.</p>',
      },
      {
        h2: '¿Cómo afecta a los recién llegados?',
        content:
          '<p>Para recién llegados latinos en Canadá, el seguro CMHC tiene implicaciones importantes:</p><ul><li><strong>Enganche mínimo del 5%:</strong> Gracias al seguro CMHC, puedes comprar con solo 5% de enganche. Sin este seguro, los bancos exigirian 20% mínimo.</li><li><strong>Programas para newcomers:</strong> Algunos bancos (RBC, Scotiabank, BMO) ofrecen hipotecas para recién llegados que permiten el 5% de enganche incluso sin historial crediticio canadiense.</li><li><strong>Costo real:</strong> Un enganche del 5% parece atractivo, pero la prima CMHC del 4% puede representar $15,000-$25,000 adicionales en tu hipoteca.</li></ul><p><strong>Consejo:</strong> Si puedes ahorrar hasta el 20% de enganche, eliminas completamente el costo del seguro CMHC. Cada punto porcentual que subas tu enganche reduce la prima.</p>',
      },
      {
        h2: 'Enganche mínimo según el precio de la vivienda',
        content:
          '<p>El gobierno de Canadá establece reglas especificas para el enganche mínimo:</p><table><tr><th>Precio de la vivienda</th><th>Enganche mínimo</th></tr><tr><td>Hasta $500,000</td><td>5% del precio total</td></tr><tr><td>$500,001 – $1,499,999</td><td>5% de los primeros $500K + 10% del resto</td></tr><tr><td>$1,500,000 o más</td><td>20% (seguro CMHC no disponible)</td></tr></table><p><strong>Ejemplo:</strong> Para una casa de $700,000, el enganche mínimo es: 5% × $500,000 = $25,000 + 10% × $200,000 = $20,000 = <strong>$45,000 total</strong>.</p><p>Las propiedades de $1.5 millones o más no califican para seguro CMHC, por lo que necesitas al menos 20% de enganche.</p>',
      },
    ],
    relatedTerms: ['amortización-canada', 'prime-rate-canada', 'fhsa-canada', 'credit-score-canada'],
    relatedArticles: [],
    affiliateWidget: {
      label: 'Calcula tu pago hipotecario con seguro CMHC',
      description: 'Usa nuestra calculadora para ver como el seguro CMHC afecta tu pago mensual según tu enganche.',
      ctaText: 'Calcular hipoteca',
      ctaLink: '/calculadora-hipoteca',
    },
    faqs: [
      {
        question: '¿Puedo evitar pagar el seguro CMHC?',
        answer: 'Si, la única forma es aportar un enganche del 20% o más del precio de compra. No hay excepciones ni exenciones.',
      },
      {
        question: '¿El seguro CMHC es reembolsable si vendo mi casa?',
        answer: 'No. Una vez pagado, el seguro CMHC no es reembolsable, incluso si vendes la propiedad antes de terminar de pagar la hipoteca.',
      },
      {
        question: '¿Puedo transferir mi seguro CMHC a otra propiedad?',
        answer: 'En algunos casos si, mediante un proceso llamado "porting". Debes mantener el mismo prestamista y cumplir las condiciones de tu hipoteca actual. Consulta con tu banco.',
      },
    ],
    author: 'Carlos Alviz',
    reviewedBy: 'Natalia Duarte',
    lastUpdated: '2026-02-21',
    impact: 'medio',
    learningPath: ['amortización-canada', 'prime-rate-canada', 'fhsa-canada'],
    comparatorLinks: [{ label: 'Calculadora hipotecaria', path: '/calculadora-hipoteca' }, { label: 'Comparar hipotecas', path: '/comparar-hipotecas' }],
    sources: [
      { label: 'CMHC - Mortgage Loan Insurance', url: 'https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance' },
      { label: 'Government of Canada - Down Payment', url: 'https://www.canada.ca/en/financial-consumer-agency/services/mortgages/down-payment.html' },
    ],
  },
];

// Lookup map for quick access by slug
const financlopediaBySlug = {};
financlopediaEntries.forEach((entry) => {
  financlopediaBySlug[entry.slug] = entry;
});

// Featured terms (shown at top of index)
const FEATURED_SLUGS = [
  'tfsa-canada',
  'rrsp-canada',
  'fhsa-canada',
  'credit-score-canada',
  't4-canada',
  'sin-number-canada',
];

/* ── Cluster lookups ─────────────────────────────────────────────────── */

const clusterBySlug = {};
CLUSTERS.forEach((cluster) => {
  cluster.slugs.forEach((slug) => {
    clusterBySlug[slug] = cluster.id;
  });
});

const clusterById = {};
CLUSTERS.forEach((cluster) => {
  clusterById[cluster.id] = cluster;
});

/* ── Smart search extras ─────────────────────────────────────────────── */

const SMART_SEARCH_EXTRAS = [
  // Blog guides
  { type: 'guide', label: 'Construir crédito desde cero', path: '/blog/construir-credito-canada-desde-cero', keywords: ['crédito', 'credito', 'construir', 'score', 'puntaje'] },
  { type: 'guide', label: 'T4 explicado en español', path: '/blog/t4-explicado-espanol-impuestos-canada', keywords: ['t4', 'impuestos', 'formulario', 'declarar'] },
  { type: 'guide', label: 'TFSA vs RRSP vs FHSA', path: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes', keywords: ['tfsa', 'rrsp', 'fhsa', 'ahorro', 'comparar', 'diferencia'] },
  { type: 'guide', label: 'Guía impuestos Quebec', path: '/blog/guia-impuestos-latinos-quebec-2026', keywords: ['quebec', 'impuestos', 'revenu', 'provincial'] },
  { type: 'guide', label: 'Primera hipoteca en Canadá', path: '/blog/primera-hipoteca-canada-guia', keywords: ['hipoteca', 'casa', 'vivienda', 'comprar', 'mortgage'] },
  { type: 'guide', label: 'Costo de vida en Montreal 2026', path: '/blog/costo-de-vida-montreal-2026', keywords: ['montreal', 'costo', 'vida', 'quebec', 'alquiler', 'rent', 'gastos', 'presupuesto'] },
  { type: 'guide', label: 'Crédito de Solidaridad Quebec', path: '/blog/solidarity-tax-credit-quebec-guia-completa', keywords: ['solidaridad', 'solidarity', 'quebec', 'crédito', 'beneficio', 'tps', 'tvq'] },
  { type: 'guide', label: 'RRSP Deadline Marzo 2026', path: '/blog/rrsp-deadline-marzo-2026', keywords: ['rrsp', 'deadline', 'plazo', 'marzo', 'contribución', 'deducción'] },
  { type: 'guide', label: 'CRA vs Revenu Québec', path: '/blog/cra-vs-revenu-quebec-diferencias', keywords: ['cra', 'revenu', 'quebec', 'diferencias', 'declarar', 'federal', 'provincial'] },
  { type: 'guide', label: 'Tarjetas para newcomers', path: '/blog/tarjetas-newcomers-que-piden-bancos', keywords: ['tarjeta', 'newcomer', 'recién', 'llegado', 'banco', 'requisitos', 'primera'] },
  { type: 'guide', label: 'Mejores cuentas sin fees', path: '/blog/mejores-cuentas-sin-fees-recien-llegados', keywords: ['cuenta', 'cuentas', 'sin', 'fees', 'gratis', 'recién', 'llegado', 'chequing'] },
  // Bank comparison guides
  { type: 'guide', label: 'RBC vs TD para recién llegados', path: '/comparar/rbc-vs-td-recien-llegados', keywords: ['rbc', 'td', 'banco', 'comparar', 'newcomer', 'recién', 'llegado'] },
  { type: 'guide', label: 'Scotiabank vs BMO para inmigrantes', path: '/comparar/scotiabank-vs-bmo-inmigrantes', keywords: ['scotiabank', 'bmo', 'banco', 'comparar', 'inmigrante'] },
  { type: 'guide', label: 'RBC vs Desjardins en Quebec', path: '/comparar/rbc-vs-desjardins-quebec', keywords: ['rbc', 'desjardins', 'quebec', 'banco', 'comparar'] },
  { type: 'guide', label: 'Mejores bancos para recién llegados 2026', path: '/comparar/mejores-bancos-recien-llegados-canada-2026', keywords: ['mejor', 'banco', 'bancos', 'recién', 'llegado', 'newcomer', '2026'] },
  // Tools
  { type: 'tool', label: 'Calculadora hipotecaria', path: '/calculadora-hipoteca', keywords: ['calculadora', 'hipoteca', 'mortgage', 'pago', 'mensual'] },
  { type: 'tool', label: 'Planificador de presupuesto', path: '/budget-planner', keywords: ['presupuesto', 'budget', 'gastos', 'ingresos', 'planificar'] },
  { type: 'tool', label: 'Calendario fiscal 2026', path: '/calendario-fiscal-2026', keywords: ['calendario', 'fiscal', 'impuestos', 'fechas', 'deadline', 'tax', 'plazo', '2026'] },
  // Comparators
  { type: 'comparator', label: 'Comparar tarjetas de crédito', path: '/tarjetas-de-credito', keywords: ['tarjeta', 'tarjetas', 'crédito', 'credito', 'comparar'] },
  { type: 'comparator', label: 'Comparar cuentas bancarias', path: '/comparar-cuentas', keywords: ['cuenta', 'cuentas', 'banco', 'bancaria', 'chequing', 'savings'] },
  { type: 'comparator', label: 'Comparar hipotecas', path: '/comparar-hipotecas', keywords: ['hipoteca', 'hipotecas', 'mortgage', 'tasa', 'rate'] },
  { type: 'comparator', label: 'Comparar remesas', path: '/comparar-remesas', keywords: ['remesa', 'remesas', 'enviar', 'dinero', 'transferencia', 'internacional'] },
  { type: 'comparator', label: 'Comparar seguros', path: '/comparar-seguros', keywords: ['seguro', 'seguros', 'insurance', 'auto', 'vida', 'hogar', 'comparar'] },
  { type: 'comparator', label: 'Tasas GIC', path: '/tasas-gic', keywords: ['gic', 'tasa', 'interés', 'plazo', 'fijo', 'guaranteed', 'inversión', 'cdic'] },
  { type: 'comparator', label: 'Cuentas registradas (TFSA, RRSP, FHSA)', path: '/cuentas-registradas', keywords: ['registrada', 'tfsa', 'rrsp', 'fhsa', 'resp', 'cuenta', 'ahorro', 'inversión'] },
  // Main pages
  { type: 'tool', label: 'Banca — encuentra tu cuenta ideal', path: '/banca', keywords: ['banca', 'banco', 'cuenta', 'banking', 'abrir', 'chequing', 'newcomer'] },
  { type: 'tool', label: 'Hipotecas — tu mejor tasa', path: '/hipotecas', keywords: ['hipoteca', 'hipotecas', 'mortgage', 'tasa', 'casa', 'comprar', 'vivienda'] },
  { type: 'tool', label: 'Empieza aquí — guía para recién llegados', path: '/empieza-aqui', keywords: ['empezar', 'empieza', 'primeros', 'pasos', 'recién', 'llegado', 'newcomer', 'guía'] },
];

export { financlopediaEntries, financlopediaBySlug, FEATURED_SLUGS, CLUSTERS, clusterBySlug, clusterById, SMART_SEARCH_EXTRAS };
export default financlopediaEntries;
